import { t } from "i18next";

import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import {
  alpha,
  Box,
  IconButton,
  type SxProps,
  type Theme,
  Typography
} from "@mui/material";

import { isDarkMode } from "@/src/theme/helpers";
import { theme } from "@/src/theme/theme";

import DropzoneUploadCard from "@/src/components/molecules/MediaUploadCard/DropzoneUploadCard";
import { UnifiedLightbox } from "@/src/components/organisms/UnifiedImageCarousel";

import { MAX_FEEDBACK_IMAGES_COUNT } from "@/src/config/baanaConfig";

const removeAttachmentButtonSx: SxProps<Theme> = (muiTheme) => ({
  position: "absolute",
  top: -8,
  right: -8,
  color: muiTheme.palette.text.primary,
  backgroundColor: muiTheme.palette.background.paper,
  border: "1px solid",
  borderColor: isDarkMode(muiTheme)
    ? alpha(muiTheme.palette.common.white, 0.4)
    : muiTheme.palette.divider,
  p: 0.25,
  "&:hover": {
    backgroundColor: muiTheme.palette.semantic.error.light,
    color: muiTheme.palette.semantic.error.contrastText
  }
});

interface FeedbackAttachmentSectionProps {
  attachments: File[];
  previewUrls: string[];
  lightboxIndex: number | null;
  isFormDisabled: boolean;
  onFileSelect: (files: FileList | File[]) => void;
  onRemove: (index: number) => void;
  onThumbnailClick: (index: number) => void;
  onLightboxClose: () => void;
  onLightboxIndexChange: (index: number) => void;
}

const FeedbackAttachmentSection = ({
  attachments,
  previewUrls,
  lightboxIndex,
  isFormDisabled,
  onFileSelect,
  onRemove,
  onThumbnailClick,
  onLightboxClose,
  onLightboxIndexChange
}: FeedbackAttachmentSectionProps) => {
  const isAtMax = attachments.length >= MAX_FEEDBACK_IMAGES_COUNT;
  // Same condition as FeedbackForm's upload progress indicator.
  const isUploadingImages = isFormDisabled && attachments.length > 0;

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ mb: 1, fontWeight: theme.typography.fontWeightMedium }}
      >
        {t("feedbackForm.fields.attachments")}
      </Typography>
      <Typography
        variant="caption"
        sx={(muiTheme) => ({
          color: isDarkMode(muiTheme)
            ? muiTheme.palette.text.primary
            : muiTheme.palette.text.secondary,
          display: "block",
          mb: 1
        })}
      >
        {t("feedbackForm.fields.attachmentHelper")}
      </Typography>
      <DropzoneUploadCard
        isLoading={isUploadingImages}
        isMaxReached={isAtMax && !isFormDisabled}
        disabled={isFormDisabled || isAtMax}
        onImageUpload={onFileSelect}
      />
      {attachments.length > 0 && (
        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {attachments.map((file, index) => (
            <Box
              key={`${file.name}-${file.size}-${index}`}
              sx={{ position: "relative" }}
            >
              <Box
                component="img"
                src={previewUrls[index]}
                alt={file.name}
                role="button"
                tabIndex={isFormDisabled ? -1 : 0}
                aria-label={`${t("feedbackForm.viewImage")} ${file.name}`}
                aria-disabled={isFormDisabled}
                onClick={() => !isFormDisabled && onThumbnailClick(index)}
                onKeyDown={(e) => {
                  if (isFormDisabled) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onThumbnailClick(index);
                  }
                }}
                sx={{
                  width: 80,
                  height: 80,
                  objectFit: "cover",
                  borderRadius: 1,
                  display: "block",
                  cursor: isFormDisabled ? "default" : "pointer",
                  "&:hover": { opacity: isFormDisabled ? 1 : 0.8 }
                }}
              />
              {!isFormDisabled && (
                <ZoomInIcon
                  sx={{
                    position: "absolute",
                    bottom: 2,
                    right: 2,
                    fontSize: 16,
                    color: theme.palette.common.white,
                    filter: `drop-shadow(0 0 2px ${theme.palette.text.primary})`,
                    pointerEvents: "none"
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={() => onRemove(index)}
                disabled={isFormDisabled}
                aria-label={`${t("feedbackForm.removeFile")} ${file.name}`}
                sx={removeAttachmentButtonSx}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      {lightboxIndex !== null && (
        <UnifiedLightbox
          images={previewUrls.map((url, i) => ({
            id: `attachment-${i}`,
            url,
            lightboxUrl: url,
            alt: attachments[i]?.name
          }))}
          open={lightboxIndex !== null}
          startIndex={lightboxIndex}
          onClose={onLightboxClose}
          onIndexChange={onLightboxIndexChange}
          showCounter={attachments.length > 1}
        />
      )}
    </Box>
  );
};

export default FeedbackAttachmentSection;
